import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { CalendarIcon, MapPinIcon, ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/24/outline';

const Experience: React.FC = () => {
  const [imageIndices, setImageIndices] = useState<{[key: number]: number}>({});

  const handleNextImage = (expIndex: number, totalImages: number) => {
    setImageIndices(prev => ({
      ...prev,
      [expIndex]: ((prev[expIndex] || 0) + 1) % totalImages
    }));
  };

  const handlePrevImage = (expIndex: number, totalImages: number) => {
    setImageIndices(prev => ({
      ...prev,
      [expIndex]: ((prev[expIndex] || 0) - 1 + totalImages) % totalImages
    }));
  };

  const experiences: {
    company: string;
    position: string;
    location: string;
    period: string;
    description: string[];
    technologies: string[];
    color: string;
    image: string | null;
    images: string[] | null;
    logo: string | null;
  }[] = [
    {
      company: "Loxport",
      position: "Senior Product Manager",
      location: "Remote",
      period: "Jan 2023 – Present",
      description: [
        "Owned the product area strategically and operationally (vision, roadmap, scope, delivery, release and success measurement) across a B2B platform (web, iOS, Android) and the underlying data and operations systems.",
        "Used SQL on the production database to find weak points, quantify their cost and base prioritisation on that evidence; ran discovery directly with operational users.",
        "Integrated LLM-based extraction, matching and document generation into existing workflows: normalised unstructured input, matched it with confidence scoring and routed anything below the threshold to human review.",
        "Built n8n and Python pipelines with schema validation, retries, error handling and monitoring to replace manual steps.",
        "Defined data model, entities, relationships, API and integration requirements together with engineering; wrote user stories with testable acceptance criteria.",
        "Led the backlog in a cross-functional Scrum team, prioritising 20+ competing topics with documented decisions on what deliberately not to build.",
        "Shipped 20+ releases with < 2% post-release defect rate and 95%+ sprint goal achievement; tested and accepted features as an ISTQB-certified tester."
      ],
      technologies: ["Product Strategy", "SQL", "LLMs", "Human-in-the-Loop", "n8n", "Python", "REST APIs", "Postman", "Scrum", "Jira"],
      color: "from-primary-500 to-blue-600",
      image: null,
      images: null,
      logo: "/companies/loxport.jpg"
    },
    {
      company: "B+P GmbH",
      position: "Project Manager",
      location: "Berlin, Germany",
      period: "Jan 2019 – Dec 2022",
      description: [
        "Led several parallel construction and infrastructure projects end to end: scope, budget, resources and schedule.",
        "Delivered projects within a regulated framework where documentation, approvals and evidence had to withstand external audits.",
        "Coordinated clients, specialist planners, contractors and authorities with differing interests and brought them to joint decisions.",
        "Owned the commercial chain: cost estimation, budget control and matching of purchase order, delivery and invoice."
      ],
      technologies: ["Project Management", "Budget Control", "Risk Management", "Stakeholder Management", "IPMA"],
      color: "from-orange-500 to-red-600",
      image: null,
      images: null,
      logo: "/companies/bp-group.jpg"
    },
    {
      company: "CR Schwabe",
      position: "Working Student – Engineering",
      location: "Berlin, Germany",
      period: "Jan 2018 – Nov 2018",
      description: [
        "Coordinated architects, structural engineers and contractors to keep requirements aligned and delivery on time.",
        "Handled communication with clients and authorities in line with regulatory requirements."
      ],
      technologies: ["Coordination", "Construction", "Compliance"],
      color: "from-teal-500 to-green-600",
      image: null,
      images: null,
      logo: null
    }
  ];

  return (
    <section id="experience" className="py-20 bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <h2 className="text-4xl font-bold text-gray-900 mb-4">
            Experience
          </h2>
          <div className="w-24 h-1 bg-primary-600 mx-auto rounded-full"></div>
          <p className="text-lg text-gray-600 mt-6 max-w-2xl mx-auto">
            From leading construction projects to owning data-driven products and AI in production
          </p>
        </motion.div>

        <div className="relative">
          {/* Timeline line */}
          <div className="hidden md:block absolute left-1/2 transform -translate-x-1/2 w-0.5 h-full bg-gray-300"></div>

          <div className="space-y-12">
            {experiences.map((exp, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: index * 0.1 }}
                viewport={{ once: true }}
                className={`relative flex items-center ${
                  index % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'
                }`}
              >
                {/* Timeline dot */}
                <div className="hidden md:block absolute left-1/2 transform -translate-x-1/2 -translate-y-1/2 top-1/2">
                  <div className={`w-4 h-4 rounded-full bg-gradient-to-r ${exp.color} border-4 border-white shadow-lg`}></div>
                </div>

                {/* Content */}
                <div className={`w-full md:w-5/12 ${index % 2 === 0 ? 'md:pr-8' : 'md:pl-8'}`}>
                  <motion.div
                    whileHover={{ scale: 1.02 }}
                    className="bg-white p-6 rounded-lg shadow-lg hover:shadow-xl transition-all duration-300 border border-gray-100 group"
                  >
                    {/* Header */}
                    <div className="mb-4">
                      {/* <div className={`inline-block p-3 rounded-lg bg-gradient-to-r ${exp.color} mb-4`}>
                        <BriefcaseIcon className="h-6 w-6 text-white" />
                      </div> */}
                      
                      <h3 className="text-xl font-bold text-gray-900 mb-2">
                        {exp.position}
                      </h3>
                      
                      <div className="flex flex-wrap items-center gap-4 text-sm text-gray-600 mb-4">
                        <div className="flex items-center">
                          {exp.logo && (
                            <img 
                              src={exp.logo} 
                              alt={`${exp.company} logo`}
                              className="w-5 h-5 object-contain mr-2"
                              onError={(e) => {
                                const target = e.target as HTMLImageElement;
                                target.style.display = 'none';
                              }}
                            />
                          )}
                          <span className="font-semibold text-primary-600">
                            {exp.company}
                          </span>
                        </div>
                        <div className="flex items-center">
                          <MapPinIcon className="h-4 w-4 mr-1" />
                          {exp.location}
                        </div>
                        <div className="flex items-center">
                          <CalendarIcon className="h-4 w-4 mr-1" />
                          {exp.period}
                        </div>
                      </div>
                    </div>

                    {/* Image/Slider */}
                    {(exp.image || exp.images) && (
                      <div className="mb-6">
                        <div className="relative rounded-lg overflow-hidden shadow-md">
                          {exp.images && Array.isArray(exp.images) && exp.images.length > 1 ? (
                            // Multiple images - show slider
                            <>
                              <AnimatePresence mode="wait">
                                <motion.img
                                  key={`${index}-${imageIndices[index] || 0}`}
                                  src={exp.images![imageIndices[index] || 0]}
                                  alt={`${exp.company} experience - Image ${(imageIndices[index] || 0) + 1}`}
                                  className="w-full h-48 object-cover"
                                  initial={{ opacity: 0, x: 300 }}
                                  animate={{ opacity: 1, x: 0 }}
                                  exit={{ opacity: 0, x: -300 }}
                                  transition={{ duration: 0.5, ease: "easeInOut" }}
                                />
                              </AnimatePresence>

                              {/* Navigation Arrows */}
                              <button
                                onClick={() => handlePrevImage(index, exp.images!.length)}
                                className="absolute left-2 top-1/2 transform -translate-y-1/2 p-2 bg-black/30 hover:bg-black/50 rounded-full text-white transition-all duration-300 opacity-0 group-hover:opacity-100"
                              >
                                <ChevronLeftIcon className="h-4 w-4" />
                              </button>
                              <button
                                onClick={() => handleNextImage(index, exp.images!.length)}
                                className="absolute right-2 top-1/2 transform -translate-y-1/2 p-2 bg-black/30 hover:bg-black/50 rounded-full text-white transition-all duration-300 opacity-0 group-hover:opacity-100"
                              >
                                <ChevronRightIcon className="h-4 w-4" />
                              </button>

                              {/* Dots Indicator */}
                              <div className="absolute bottom-2 left-1/2 transform -translate-x-1/2 flex space-x-1">
                                {exp.images!.map((_, imgIndex) => (
                                  <button
                                    key={imgIndex}
                                    onClick={() => setImageIndices(prev => ({...prev, [index]: imgIndex}))}
                                    className={`w-2 h-2 rounded-full transition-all duration-300 ${
                                      imgIndex === (imageIndices[index] || 0)
                                        ? 'bg-white w-6'
                                        : 'bg-white/50 hover:bg-white/70'
                                    }`}
                                  />
                                ))}
                              </div>
                            </>
                          ) : (
                            // Single image
                            <img 
                              src={exp.image || (exp.images && exp.images[0]) || ''} 
                              alt={`${exp.company} experience`}
                              className="w-full h-48 object-cover hover:scale-105 transition-transform duration-500"
                            />
                          )}
                        </div>
                      </div>
                    )}

                    {/* Description */}
                    <div className="mb-6">
                      <ul className="space-y-2">
                        {exp.description.map((desc, descIndex) => (
                          <li key={descIndex} className="text-gray-600 leading-relaxed flex items-start">
                            <span className="text-primary-600 mr-2 mt-2">•</span>
                            {desc}
                          </li>
                        ))}
                      </ul>
                    </div>

                    {/* Technologies */}
                    <div className="flex flex-wrap gap-2">
                      {exp.technologies.map((tech, techIndex) => (
                        <span
                          key={techIndex}
                          className="px-3 py-1 bg-gray-100 text-gray-800 text-sm rounded-full border border-gray-200 hover:bg-gray-200 transition-all duration-200"
                        >
                          {tech}
                        </span>
                      ))}
                    </div>
                  </motion.div>
                </div>

                {/* Spacer for the other half on desktop */}
                <div className="hidden md:block w-5/12"></div>
              </motion.div>
            ))}
          </div>
        </div>

        {/* Call to Action */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          className="text-center mt-16"
        >
          <div className="bg-gradient-to-r from-primary-50 to-blue-50 p-8 rounded-lg max-w-2xl mx-auto">
            <h3 className="text-2xl font-bold text-gray-900 mb-4">
              Ready to Collaborate?
            </h3>
            <p className="text-gray-600 mb-6">
              I'm always open to talking about product roles, data-driven products and AI that works in real operations.
            </p>
            <motion.a
              href="#contact"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="inline-flex items-center px-6 py-3 bg-primary-600 hover:bg-primary-700 text-white font-medium rounded-lg transition-colors shadow-lg hover:shadow-xl"
            >
              Let's Work Together
            </motion.a>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default Experience;